import { MotionProps } from 'framer-motion';
import { ReactElement, ReactNode, UIEvent, useEffect, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconNames } from '@sendible/design-system/src/theme';
import { Backdrop, Container, Content, Footer, Header, HeaderActions, HeaderTitle, SubHeaderContent, Wrapper } from './index.styles';
import { Button } from '../Button';
import { Heading } from '../../atoms/Heading';
import { Icon } from '../../atoms/Icon';
import { useKeyDown } from '../../utils/useKeyDown';

export const animations = ['appear', 'bottom', 'fade', 'left', 'right', 'top'] as const;
export const variations = ['fullscreen', 'small', 'medium', 'large', 'right'] as const;

type Animations = {
  [key in (typeof animations)[number]]: MotionProps;
};

export interface ButtonProps {
  /**
   * Action event emmiter. Required.
   */
  action: () => void;
  /**
   * Action button label. Required.
   */
  actionLabel: string;
  /**
   * Disabled prop. Optional.
   */
  isDisabled?: boolean;
  /**
   * Set button to loading state. Optional.
   */
  isLoading?: boolean;
}

export interface ModalProps extends Component {
  /**
   * Animation type. Optional.
   */
  animation?: (typeof animations)[number];
  /**
   * Animation speed. Try values between 0 and 1 for better results. Optional.
   */
  animationSpeed?: number;
  /**
   * Aria close label. Optional.
   */
  ariaCloseLabel?: string;
  /**
   * Button details. Optional.
   */
  button?: ButtonProps;
  /**
   * Close event emmiter.
   * NOTE: Using both footerContent and
   * button props will cause button overlap
   */
  close: () => void;
  /**
   * footer content. Optional.
   * NOTE: Using both footerContent and
   * button props will cause button overlap
   */
  footerContent?: string | ReactElement;
  /**
   * Header action slot. Appears close the the close button. Optional.
   */
  headingAction?: ReactElement;
  /**
   * Heading icon name. If it has icon, icon will be applied. Optional. (You can see the full list at branding/icons)
   */
  headingIcon?: IconNames;
  /**
   * Header title. Optional.
   */
  headingTitle?: string | ReactElement;
  /**
   * Hide header. Optional.
   */
  hideHeader?: boolean;
  /**
   * Do not close when pressing Esc key. Optional.
   */
  preventEscapeClose?: boolean;
  /**
   * Slot for content under the header. Optional.
   */
  subHeaderContent?: ReactNode;
  /**
   * Variation name. Optional.
   */
  variation?: (typeof variations)[number];
  /**
   * Portal DOM ID to use for createPortal. Optional.
   */
  portalDomId?: string;
  /**
   * Z-Index for the modal container. Optional.
   */
  zIndex?: number | null;
}

/**
 * Modal
 */
export const Modal = (props: ModalProps) => {
  const {
    animation = 'fade',
    animationSpeed = 0.3,
    ariaCloseLabel = '',
    button,
    children,
    className = '',
    close,
    customClass = '',
    footerContent,
    headingAction,
    headingIcon,
    headingTitle,
    hideHeader = false,
    preventEscapeClose = false,
    subHeaderContent,
    testId = 'modal',
    variation = 'fullscreen',
    portalDomId = 'modal-root',
    zIndex = null,
  } = props;

  const [scrolled, setScrolled] = useState(false);
  const [extraHeight, setExtraHeight] = useState(0);

  const headerRef = useRef<HTMLElement>(null);
  const subheaderRef = useRef<HTMLDivElement>(null);
  const footer1Ref = useRef<HTMLElement>(null);
  const footer2Ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = document.getElementById(portalDomId);

    if (zIndex !== null && el) {
      el.style.setProperty('z-index', String(zIndex));
    }

    return () => {
      if (zIndex !== null && el) {
        el.style.removeProperty('z-index');
      }
    };
  }, [zIndex, portalDomId]);

  // REACT 19 / LINTER FIX: Compute layout elements cleanly within a useLayoutEffect
  // instead of passing untracked node definitions inside dependencies.
  useLayoutEffect(() => {
    const param1 = headerRef.current?.offsetHeight || 0;
    const param2 = subheaderRef.current?.offsetHeight || 0;
    const param3 = footer1Ref.current?.offsetHeight || 0;
    const param4 = footer2Ref.current?.offsetHeight || 0;

    setExtraHeight(param1 + param2 + param3 + param4);
  }, [children, button, footerContent, subHeaderContent, hideHeader]);

  const animationsSettings: Animations = {
    appear: {
      initial: { scale: 1.2, opacity: 0 },
      animate: { scale: 1, opacity: 1 },
      exit: { scale: 1.2, opacity: 0 },
    },
    bottom: {
      initial: { y: '100vh', opacity: 0 },
      animate: { y: 0, opacity: 1 },
      exit: { y: '100vh', opacity: 0 },
    },
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    left: {
      initial: { x: '-100vw', opacity: 0 },
      animate: { x: 0, opacity: 1 },
      exit: { x: '-100vw', opacity: 0 },
    },
    right: {
      initial: { x: '100vw', opacity: 0 },
      animate: { x: 0, opacity: 1 },
      exit: { x: '100vw', opacity: 0 },
    },
    top: {
      initial: { y: '-100vh', opacity: 0 },
      animate: { y: 0, opacity: 1 },
      exit: { y: '-100vh', opacity: 0 },
    },
  };

  const onScroll = (e: UIEvent) => {
    const target = e.target as HTMLElement;

    setScrolled(target.scrollTop > 0);
  };

  useKeyDown(() => {
    if (!preventEscapeClose) {
      close();
    }
  }, 'Escape');

  const portalTarget = document.getElementById(portalDomId);

  if (!portalTarget) return null;

  return createPortal(
    <Container
      className={`ds-modal ds-modal-${variation} ${customClass} ${className}`}
      data-testid={testId}
      key="ds-modal"
      $variation={variation}
    >
      <Wrapper
        $variation={variation}
        initial={animationsSettings[animation].initial}
        animate={animationsSettings[animation].animate}
        exit={animationsSettings[animation].exit}
        transition={{ ease: 'easeInOut', duration: 0.33 }}
        key="ds-modal-wrapper"
      >
        {!hideHeader && (
          <>
            <Header
              ref={headerRef}
              showLine={!subHeaderContent && scrolled}
              $variation={variation}
            >
              <HeaderTitle $variation={variation}>
                {headingIcon && (
                  <Icon
                    color="brandDark"
                    name={headingIcon}
                  />
                )}
                {typeof headingTitle === 'string' ? (
                  <Heading
                    align="left"
                    color="brandDark"
                    customClass="modal-heading"
                    maxLines={1}
                    semantic="h5"
                    variation={variation === 'large' ? 'component-title_20' : 'component-title_14'}
                  >
                    {headingTitle}
                  </Heading>
                ) : (
                  headingTitle
                )}
              </HeaderTitle>
              <HeaderActions>
                {headingAction || null}
                {variation !== 'right' && (
                  <Button
                    onClick={close}
                    icon="close"
                    size={16}
                    aria-label={ariaCloseLabel}
                  />
                )}
              </HeaderActions>
            </Header>
            {subHeaderContent && (
              <SubHeaderContent
                ref={subheaderRef}
                showLine={scrolled}
              >
                {subHeaderContent}
              </SubHeaderContent>
            )}
          </>
        )}
        {children && (
          <Content
            $extraHeight={extraHeight}
            onScroll={onScroll}
            $variation={variation}
          >
            {children}
          </Content>
        )}
        {button && (
          <Footer
            ref={footer1Ref}
            showLine={scrolled}
          >
            <Button
              appearance="primary"
              onClick={button.action}
              label={button.actionLabel}
              disabled={button.isDisabled}
              isLoading={button.isLoading}
              size={18}
              variation="fill"
            />
          </Footer>
        )}
        {footerContent && (
          <Footer
            footerVisible
            ref={footer2Ref}
          >
            {footerContent}
          </Footer>
        )}
      </Wrapper>
      <Backdrop
        data-testid="backdrop"
        onClick={close}
        $variation={variation}
        transition={{ duration: animationSpeed * 1.66, ease: 'easeInOut' }}
        initial={animationsSettings.fade.initial}
        animate={animationsSettings.fade.animate}
        exit={animationsSettings.fade.exit}
        key="ds-modal-backdrop"
      />
    </Container>,
    portalTarget
  );
};
